/**
 * API Reference Documentation - QSL (Quanthop Strategy Language)
 */
import type { DocPage } from '../types';

export const apiDocs: DocPage[] = [
  // Lifecycle - define()
  {
    id: 'api/lifecycle/define',
    title: 'define()',
    category: 'api',
    subcategory: 'lifecycle',
    tags: ['lifecycle', 'define', 'parameters', 'setup', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/init', 'api/lifecycle/onBar', 'getting-started/strategy-lifecycle'],
    order: 1,
    description: 'Declare strategy parameters',
    content: `# define()

Declare your strategy parameters. Called once when the strategy loads.

## Signature

\`\`\`javascript
function define(ctx)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`ctx\` | \`DefineContext\` | Context for parameter declaration |

## ctx.param()

Use \`ctx.param()\` to declare each parameter:

\`\`\`javascript
ctx.param(name, options)
\`\`\`

### Parameter Options

| Option | Type | Required | Description |
|--------|------|----------|-------------|
| \`type\` | \`string\` | Yes | \`'int'\`, \`'float'\`, \`'string'\`, \`'boolean'\` |
| \`label\` | \`string\` | No | Display label in UI |
| \`description\` | \`string\` | No | Tooltip text |
| \`default\` | \`any\` | Yes | Default value |
| \`min\` | \`number\` | No | Minimum (int/float) |
| \`max\` | \`number\` | No | Maximum (int/float) |
| \`step\` | \`number\` | No | Step size for sliders/optimization |
| \`optimize\` | \`boolean\` | No | Include in optimization runs |
| \`group\` | \`string\` | No | UI grouping |
| \`options\` | \`array\` | No | For string type: enum values |

## Example

\`\`\`javascript
function define(ctx) {
  ctx.param('fastLength', {
    type: 'int',
    label: 'Fast EMA',
    description: 'Period for the fast EMA',
    default: 9,
    min: 5,
    max: 50,
    step: 1,
    optimize: true,
    group: 'Trend'
  });
  
  ctx.param('slowLength', {
    type: 'int',
    label: 'Slow EMA',
    default: 21,
    min: 10,
    max: 200,
    optimize: true,
    group: 'Trend'
  });
  
  ctx.param('riskPercent', {
    type: 'float',
    label: 'Risk %',
    default: 0.02,
    min: 0.01,
    max: 0.10,
    step: 0.01,
    group: 'Risk'
  });
  
  ctx.param('direction', {
    type: 'string',
    label: 'Trade Direction',
    default: 'long',
    options: ['long', 'short', 'both']
  });
}
\`\`\`

## Parameter Types

### int

Integer numbers. Use for periods, counts, lookbacks.

\`\`\`javascript
ctx.param('period', { type: 'int', default: 14, min: 2, max: 100 });
\`\`\`

### float

Decimal numbers. Use for percentages, multipliers.

\`\`\`javascript
ctx.param('threshold', { type: 'float', default: 0.5, min: 0.1, max: 1.0, step: 0.1 });
\`\`\`

### string

Text or enum values.

\`\`\`javascript
ctx.param('mode', { 
  type: 'string', 
  default: 'aggressive',
  options: ['conservative', 'moderate', 'aggressive']
});
\`\`\`

### boolean

True/false toggles.

\`\`\`javascript
ctx.param('useFilter', { type: 'boolean', default: true, label: 'Use Trend Filter' });
\`\`\`

## Accessing Parameters

Parameters are available in \`init()\` and \`onBar()\` via \`ctx.p\`:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: ctx.p.fastLength });
}

function onBar(ctx, i) {
  if (ctx.p.useFilter && ctx.ind.ema[i] > ctx.series.close[i]) {
    return; // Skip if filter is active and price below EMA
  }
}
\`\`\`

## Related

- [init()](/docs/api/lifecycle/init) — Indicator declaration
- [onBar()](/docs/api/lifecycle/onBar) — Trading logic
- [Strategy Lifecycle](/docs/getting-started/strategy-lifecycle) — Execution order
`,
  },

  // Lifecycle - init()
  {
    id: 'api/lifecycle/init',
    title: 'init()',
    category: 'api',
    subcategory: 'lifecycle',
    tags: ['lifecycle', 'init', 'indicators', 'state', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/define', 'api/lifecycle/onBar', 'api/indicators/sma'],
    order: 2,
    description: 'Declare indicators and initialize state',
    content: `# init()

Declare indicators and initialize state. Called once before the backtest starts.

## Signature

\`\`\`javascript
function init(ctx)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`ctx\` | \`InitContext\` | Context for indicator declaration and state |

## ctx.indicator()

Declare indicators that will be pre-computed by the engine:

\`\`\`javascript
ctx.indicator(name, type, options)
\`\`\`

| Argument | Type | Description |
|----------|------|-------------|
| \`name\` | \`string\` | Unique name to access via \`ctx.ind.name\` |
| \`type\` | \`string\` | Indicator type (see available indicators) |
| \`options\` | \`object\` | Indicator-specific options |

**Key insight**: Indicators are computed by the engine on the host side *before* \`onBar\` runs. This is much faster than computing them yourself.

### Display Option

Add \`display: true\` to show the indicator on the chart:

\`\`\`javascript
// This indicator will appear on the price chart
ctx.indicator('ema', 'EMA', { period: 21, source: 'close', display: true });

// This indicator is computed but NOT shown on chart (default)
ctx.indicator('rsi', 'RSI', { period: 14 });
\`\`\`

## Example

\`\`\`javascript
function init(ctx) {
  // Trend indicators - shown on chart
  ctx.indicator('fastEma', 'EMA', { period: ctx.p.fastLength, source: 'close', display: true });
  ctx.indicator('slowEma', 'EMA', { period: ctx.p.slowLength, source: 'close', display: true });
  
  // Momentum - not displayed (used for signal logic only)
  ctx.indicator('rsi', 'RSI', { period: 14 });
  ctx.indicator('macd', 'MACD', { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 });
  
  // Volatility
  ctx.indicator('atr', 'ATR', { period: 14 });
  ctx.indicator('bbands', 'BBANDS', { period: 20, stdDev: 2 });
  
  // Initialize state
  ctx.state.tradeCount = 0;
  ctx.state.lastSignalBar = -1;
  ctx.state.inPosition = false;
}
\`\`\`

## Available Indicators

### Moving Averages

| Indicator | Options | Output |
|-----------|---------|--------|
| [\`SMA\`](/docs/api/indicators/sma) | \`period\`, \`source\` | \`number[]\` |
| [\`EMA\`](/docs/api/indicators/ema) | \`period\`, \`source\` | \`number[]\` |
| [\`WMA\`](/docs/api/indicators/wma) | \`period\`, \`source\` | \`number[]\` |
| [\`DEMA\`](/docs/api/indicators/dema) | \`period\`, \`source\` | \`number[]\` |
| [\`TEMA\`](/docs/api/indicators/tema) | \`period\`, \`source\` | \`number[]\` |

### Trend

| Indicator | Options | Output |
|-----------|---------|--------|
| [\`MACD\`](/docs/api/indicators/macd) | \`fastPeriod\`, \`slowPeriod\`, \`signalPeriod\` | \`{ line, signal, histogram }\` |
| [\`ADX\`](/docs/api/indicators/adx) | \`period\` | \`{ adx, pdi, mdi }\` |
| [\`PSAR\`](/docs/api/indicators/psar) | \`step\`, \`max\` | \`number[]\` |
| [\`ICHIMOKU\`](/docs/api/indicators/ichimoku) | \`tenkanPeriod\`, \`kijunPeriod\`, \`senkouBPeriod\` | \`{ tenkan, kijun, senkouA, senkouB, chikou }\` |
| [\`TRIX\`](/docs/api/indicators/trix) | \`period\`, \`source\` | \`number[]\` |

### Momentum / Oscillators

| Indicator | Options | Output |
|-----------|---------|--------|
| [\`RSI\`](/docs/api/indicators/rsi) | \`period\`, \`source\` | \`number[]\` |
| [\`STOCH\`](/docs/api/indicators/stoch) | \`kPeriod\`, \`dPeriod\` | \`{ k, d }\` |
| [\`STOCHRSI\`](/docs/api/indicators/stochrsi) | \`rsiPeriod\`, \`stochPeriod\`, \`kPeriod\`, \`dPeriod\` | \`{ k, d }\` |
| [\`CCI\`](/docs/api/indicators/cci) | \`period\` | \`number[]\` |
| [\`WILLIAMSR\`](/docs/api/indicators/williamsr) | \`period\` | \`number[]\` |
| [\`ROC\`](/docs/api/indicators/roc) | \`period\`, \`source\` | \`number[]\` |
| [\`AO\`](/docs/api/indicators/ao) | \`fastPeriod\`, \`slowPeriod\` | \`number[]\` |
| [\`PPO\`](/docs/api/indicators/ppo) | \`shortGamma\`, \`longGamma\`, \`accuracy\` | \`number[]\` |

### Volatility

| Indicator | Options | Output |
|-----------|---------|--------|
| [\`BBANDS\`](/docs/api/indicators/bbands) | \`period\`, \`stdDev\` | \`{ upper, middle, lower }\` |
| [\`ATR\`](/docs/api/indicators/atr) | \`period\` | \`number[]\` |
| [\`DONCHIAN\`](/docs/api/indicators/donchian) | \`period\` | \`{ upper, middle, lower }\` |
| [\`KELTNER\`](/docs/api/indicators/keltner) | \`period\`, \`multiplier\` | \`{ upper, middle, lower }\` |

### Volume

| Indicator | Options | Output |
|-----------|---------|--------|
| [\`OBV\`](/docs/api/indicators/obv) | — | \`number[]\` |
| [\`MFI\`](/docs/api/indicators/mfi) | \`period\` | \`number[]\` |
| [\`VWAP\`](/docs/api/indicators/vwap) | — | \`number[]\` |
| [\`ADL\`](/docs/api/indicators/adl) | — | \`number[]\` |

### Source Options

The \`source\` option can be:

- \`'open'\`, \`'high'\`, \`'low'\`, \`'close'\`, \`'volume'\`
- \`'hl2'\` — (high + low) / 2
- \`'hlc3'\` — (high + low + close) / 3
- \`'ohlc4'\` — (open + high + low + close) / 4

## ctx.state

Initialize mutable state that persists across bars:

\`\`\`javascript
function init(ctx) {
  ctx.state.consecutiveLosses = 0;
  ctx.state.peakEquity = 0;
  ctx.state.lastEntryPrice = null;
}
\`\`\`

State is accessed in \`onBar\` via \`ctx.state\`:

\`\`\`javascript
function onBar(ctx, i) {
  ctx.state.consecutiveLosses++;
}
\`\`\`

## Multi-Output Indicators

Some indicators return multiple series:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('macd', 'MACD', { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 });
  ctx.indicator('bbands', 'BBANDS', { period: 20, stdDev: 2 });
}

function onBar(ctx, i) {
  // Access MACD components
  const macdLine = ctx.ind.macd.macd[i];
  const signalLine = ctx.ind.macd.signal[i];
  const histogram = ctx.ind.macd.histogram[i];
  
  // Access Bollinger Bands
  const upper = ctx.ind.bbands.upper[i];
  const middle = ctx.ind.bbands.middle[i];
  const lower = ctx.ind.bbands.lower[i];
}
\`\`\`

## Common Mistakes

### Computing indicators in onBar

\`\`\`javascript
// WRONG - Don't compute indicators yourself
function onBar(ctx, i) {
  const ema = calculateEma(ctx.series.close, 20); // Slow!
}

// RIGHT - Declare in init, engine computes them
function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: 20 });
}
\`\`\`

### Forgetting to initialize state

\`\`\`javascript
// WRONG - state.count is undefined on first bar
function onBar(ctx, i) {
  ctx.state.count++; // NaN!
}

// RIGHT - Initialize in init
function init(ctx) {
  ctx.state.count = 0;
}
\`\`\`

## Related

- [define()](/docs/api/lifecycle/define) — Parameter declaration
- [onBar()](/docs/api/lifecycle/onBar) — Trading logic
- [Available Indicators](/docs/api/indicators/sma) — Full indicator reference
`,
  },

  // Lifecycle - onBar()
  {
    id: 'api/lifecycle/onBar',
    title: 'onBar()',
    category: 'api',
    subcategory: 'lifecycle',
    tags: ['lifecycle', 'onBar', 'trading', 'logic', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/init', 'api/orders/market', 'api/helpers/overview'],
    order: 3,
    description: 'Main trading logic called for each bar',
    content: `# onBar()

Main strategy hook called for each candle in the dataset.

## Signature

\`\`\`javascript
function onBar(ctx, i)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`ctx\` | \`BarContext\` | Context with indicators, price data, orders |
| \`i\` | \`number\` | Current bar index (0-based) |

## Execution Timing

- Called **after** each bar closes
- Runs for every candle in the backtest range (i = 0 to N-1)
- Orders placed here execute on the **next bar's open**

## Available Context

\`\`\`javascript
function onBar(ctx, i) {
  // Parameters (from define)
  ctx.p.fastLength      // Your parameters
  
  // Indicators (from init, pre-computed)
  ctx.ind.ema[i]        // Single-output indicator
  ctx.ind.macd.macd[i]  // Multi-output indicator
  
  // Price series
  ctx.series.open[i]
  ctx.series.high[i]
  ctx.series.low[i]
  ctx.series.close[i]
  ctx.series.volume[i]
  
  // Position info
  ctx.position('ASSET')  // Returns { qty, avgPrice, side }
  
  // Order placement
  ctx.order.market(...)
  ctx.order.close(...)
  ctx.order.reduce(...)
  
  // Mutable state
  ctx.state.myValue
}
\`\`\`

## Example

\`\`\`javascript
function onBar(ctx, i) {
  const fast = ctx.ind.fastEma[i];
  const slow = ctx.ind.slowEma[i];
  
  // Skip if indicators not ready (warm-up period)
  if (q.isNaN(fast) || q.isNaN(slow)) return;
  
  const pos = ctx.position('ASSET');
  
  // Entry: bullish crossover
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty === 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ema_cross' });
  }
  
  // Exit: bearish crossover
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i) && pos.qty > 0) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'ema_cross' });
  }
}
\`\`\`

## Order Timing

**Critical concept**: Orders placed in \`onBar\` do not fill immediately.

\`\`\`
Bar i closes → onBar(ctx, i) runs → Order queued
Bar i+1 opens → Order fills at open price (± slippage)
\`\`\`

This models realistic execution where you cannot act within a closed bar.

## Common Patterns

### Check indicator readiness

\`\`\`javascript
function onBar(ctx, i) {
  // Indicators have warm-up periods
  if (q.isNaN(ctx.ind.ema[i])) return;
  
  // Safe to use indicators now
}
\`\`\`

### Position-aware logic

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  if (pos.qty === 0) {
    // Flat - look for entries
  } else if (pos.qty > 0) {
    // Long - manage position
  } else if (pos.qty < 0) {
    // Short - manage position
  }
}
\`\`\`

### Using previous bar data

\`\`\`javascript
function onBar(ctx, i) {
  if (i < 1) return; // Need at least 2 bars
  
  const prevClose = ctx.series.close[i - 1];
  const currClose = ctx.series.close[i];
  
  if (currClose > prevClose) {
    // Price went up
  }
}
\`\`\`

## Common Mistakes

### Expecting immediate fills

\`\`\`javascript
// WRONG - Position unchanged until next bar
function onBar(ctx, i) {
  ctx.order.market('ASSET', 1);
  const pos = ctx.position('ASSET');
 // Still 0!
}
\`\`\`

### Accessing future data

\`\`\`javascript
// WRONG - i+1 may not exist yet
function onBar(ctx, i) {
  const tomorrow = ctx.series.close[i + 1]; // undefined or error
}
\`\`\`

### Forgetting warm-up check

\`\`\`javascript
// WRONG - ema[i] is NaN for first N bars
function onBar(ctx, i) {
  if (ctx.ind.ema[i] > ctx.series.close[i]) { // NaN comparison!
}

// RIGHT
function onBar(ctx, i) {
  if (q.isNaN(ctx.ind.ema[i])) return;
  // Now safe
}
\`\`\`

## Related

- [init()](/docs/api/lifecycle/init) — Indicator declaration
- [Order API](/docs/api/orders/market) — Placing orders
- [Helper Functions](/docs/api/helpers/overview) — q.* utilities
`,
  },

  // Orders - market
  {
    id: 'api/orders/market',
    title: 'ctx.order.market()',
    category: 'api',
    subcategory: 'orders',
    tags: ['orders', 'market', 'buy', 'sell', 'entry', 'qsl'],
    version: '2.0',
    related: ['api/orders/close', 'api/orders/reduce', 'api/orders/limit', 'engine/fills/marketOrders'],
    order: 1,
    description: 'Place market orders',
    content: `# ctx.order.market()

Place a market order that fills at the next bar's open.

## Signature

\`\`\`javascript
ctx.order.market(symbol, quantity, metadata?)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`symbol\` | \`string\` | Asset symbol (typically \`'ASSET'\`) |
| \`quantity\` | \`number\` | Position size: 1 = full position, 0.5 = half, -1 = short |
| \`metadata\` | \`object\` | Optional order metadata |

### Quantity

- \`1\` = Full long position (100% of capital)
- \`0.5\` = Half position (50% of capital)
- \`0.25\` = Quarter position
- \`-1\` = Full short position
- \`-0.5\` = Half short position

### Metadata Options

| Option | Type | Description |
|--------|------|-------------|
| \`signal\` | \`string\` | Signal name for analysis (\`'buy'\`, \`'sell'\`, etc.) |
| \`reason\` | \`string\` | Human-readable reason |
| \`type\` | \`string\` | Order type hint (\`'full_entry'\`, \`'partial_entry'\`) |

## Examples

### Full entry

\`\`\`javascript
function onBar(ctx, i) {
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ema_crossover' });
  }
}
\`\`\`

### Partial entries (scaling in)

\`\`\`javascript
function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  
  if (rsi < 20) {
    // Extreme oversold: half position
    ctx.order.market('ASSET', 0.5, { signal: 'buy', type: 'partial_entry' });
  } else if (rsi < 30) {
    // Oversold: quarter position
    ctx.order.market('ASSET', 0.25, { signal: 'buy', type: 'partial_entry' });
  }
}
\`\`\`

### Short positions

Use negative quantity to open short positions. Your strategy must explicitly handle short entries and exits in its \`onBar\` function — the engine does not auto-invert long signals.

\`\`\`javascript
function onBar(ctx, i) {
  const tradingMode = ctx.p.tradingMode || 'LONG_ONLY';
  
  if (tradingMode === 'SHORT_ONLY') {
    // Enter short on bearish cross
    if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
      ctx.order.market('ASSET', -1, { signal: 'enterShort' });
    }
    // Exit short on bullish cross
    if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
      ctx.order.close('ASSET', { signal: 'exitShort' });
    }
  }
}
\`\`\`

> **Important:** Strategies must natively implement the trading modes they support. The engine does not auto-invert signals. Unsupported modes are locked in the UI. See the **Trading Modes** guide under Best Practices for full details.

## Fill Timing

Market orders fill on the **next bar's open**:

\`\`\`
Bar i: close = 100, onBar places market order
Bar i+1: open = 101 → Order fills at 101 (± slippage)
\`\`\`

## Slippage

Slippage is applied based on backtest configuration:

\`\`\`
Fill Price = Next Bar Open × (1 + slippage × direction)
\`\`\`

Where direction is +1 for buys (slippage works against you), -1 for sells.

## Related

- [ctx.order.close()](/docs/api/orders/close) — Close position
- [ctx.order.reduce()](/docs/api/orders/reduce) — Partial exit
- [Market Fill Rules](/docs/engine/fills/marketOrders) — Engine execution
`,
  },

  // Orders - close
  {
    id: 'api/orders/close',
    title: 'ctx.order.close()',
    category: 'api',
    subcategory: 'orders',
    tags: ['orders', 'close', 'exit', 'position', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/reduce'],
    order: 2,
    description: 'Close the entire position',
    content: `# ctx.order.close()

Close the entire current position.

## Signature

\`\`\`javascript
ctx.order.close(symbol, metadata?)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`symbol\` | \`string\` | Asset symbol |
| \`metadata\` | \`object\` | Optional order metadata |

## Behavior

- If long → places market sell for full position
- If short → places market buy to cover
- If flat → no order placed

## Example

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const rsi = ctx.ind.rsi[i];
  
  // Exit long on overbought
  if (pos.qty > 0 && rsi > 70) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'rsi_overbought' });
  }
  
  // Exit short on oversold
  if (pos.qty < 0 && rsi < 30) {
    ctx.order.close('ASSET', { signal: 'cover', reason: 'rsi_oversold' });
  }
}
\`\`\`

## Common Patterns

### Crossover exit

\`\`\`javascript
if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
  ctx.order.close('ASSET', { signal: 'sell' });
}
\`\`\`

### Time-based exit

\`\`\`javascript
if (i - ctx.state.entryBar >= 20) {
  ctx.order.close('ASSET', { signal: 'timeout', reason: '20_bars' });
}
\`\`\`

### Stop loss (manual)

\`\`\`javascript
const pos = ctx.position('ASSET');
if (pos.qty > 0) {
  const pnl = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice;
  if (pnl < -0.02) {
    ctx.order.close('ASSET', { signal: 'stop_loss' });
  }
}
\`\`\`

## Related

- [ctx.order.market()](/docs/api/orders/market) — Entry orders
- [ctx.order.reduce()](/docs/api/orders/reduce) — Partial exits
`,
  },

  // Orders - reduce
  {
    id: 'api/orders/reduce',
    title: 'ctx.order.reduce()',
    category: 'api',
    subcategory: 'orders',
    tags: ['orders', 'reduce', 'partial', 'exit', 'qsl'],
    version: '2.0',
    related: ['api/orders/close', 'api/orders/market'],
    order: 3,
    description: 'Partially close a position',
    content: `# ctx.order.reduce()

Reduce the current position by a percentage.

## Signature

\`\`\`javascript
ctx.order.reduce(symbol, fraction, metadata?)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`symbol\` | \`string\` | Asset symbol |
| \`fraction\` | \`number\` | Fraction to close (0.5 = 50%) |
| \`metadata\` | \`object\` | Optional order metadata |

## Example

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  if (pos.qty > 0) {
    const pnl = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice;
    
    // Take profit at 5%: close half
    if (pnl > 0.05 && !ctx.state.tookProfit) {
      ctx.order.reduce('ASSET', 0.5, { signal: 'takeProfit', reason: '5pct_gain' });
      ctx.state.tookProfit = true;
    }
    
    // Take profit at 10%: close remaining
    if (pnl > 0.10) {
      ctx.order.close('ASSET', { signal: 'takeProfit', reason: '10pct_gain' });
    }
  }
}
\`\`\`

## Scaling Out Pattern

\`\`\`javascript
function init(ctx) {
  ctx.state.scaleOutLevel = 0;
}

function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  if (pos.qty === 0) {
    ctx.state.scaleOutLevel = 0;
    return;
  }
  
  const pnl = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice;
  
  // Scale out at 3%, 6%, 9%
  if (pnl > 0.03 && ctx.state.scaleOutLevel === 0) {
    ctx.order.reduce('ASSET', 0.33, { signal: 'scale_out_1' });
    ctx.state.scaleOutLevel = 1;
  }
  if (pnl > 0.06 && ctx.state.scaleOutLevel === 1) {
    ctx.order.reduce('ASSET', 0.5, { signal: 'scale_out_2' });
    ctx.state.scaleOutLevel = 2;
  }
  if (pnl > 0.09 && ctx.state.scaleOutLevel === 2) {
    ctx.order.close('ASSET', { signal: 'scale_out_final' });
  }
}
\`\`\`

## Related

- [ctx.order.close()](/docs/api/orders/close) — Full exit
- [ctx.order.market()](/docs/api/orders/market) — Entry orders
`,
  },

  // Orders - limit
  {
    id: 'api/orders/limit',
    title: 'ctx.order.limit()',
    category: 'api',
    subcategory: 'orders',
    tags: ['orders', 'limit', 'entry', 'price', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/close', 'api/orders/reduce'],
    order: 4,
    description: 'Place a limit order at a specific price',
    content: `# ctx.order.limit()

Place a limit order that fills only when price reaches a specified level.

## Signature

\`\`\`javascript
ctx.order.limit(symbol, quantity, price, metadata?)
\`\`\`

## Parameters

| Name | Type | Description |
|------|------|-------------|
| \`symbol\` | \`string\` | Asset symbol (typically \`'ASSET'\`) |
| \`quantity\` | \`number\` | Position size (positive for long, negative for short) |
| \`price\` | \`number\` | The limit price at which the order should fill |
| \`metadata\` | \`object\` | Optional order metadata |

## How Limit Orders Work

Unlike market orders which fill at the next bar's open, limit orders only fill when the price reaches your specified level:

- **Long limit**: Fills when price drops to or below the limit price
- **Short limit**: Fills when price rises to or above the limit price

If the price never reaches the limit level, the order is not executed.

## Examples

### Buy the dip

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const close = ctx.series.close[i];
  const atr = ctx.ind.atr[i];
  
  if (pos.qty === 0) {
    // Place limit order 1 ATR below current close
    const limitPrice = close - atr;
    ctx.order.limit('ASSET', 1, limitPrice, { 
      signal: 'buy', 
      reason: 'atr_dip' 
    });
  }
}
\`\`\`

### Support-level entry

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  const support = ctx.ind.donchian_lower[i];
  
  if (pos.qty === 0) {
    // Enter long at Donchian lower band (support)
    ctx.order.limit('ASSET', 1, support, { 
      signal: 'buy', 
      reason: 'support_entry' 
    });
  }
}
\`\`\`

### Limit vs Market

\`\`\`javascript
function onBar(ctx, i) {
  // Market order: fills at next bar's open regardless of price
  ctx.order.market('ASSET', 1, { signal: 'buy' });
  
  // Limit order: only fills if price reaches the specified level
  ctx.order.limit('ASSET', 1, 50000, { signal: 'buy' });
}
\`\`\`

## Related

- [ctx.order.market()](/docs/api/orders/market) — Market orders (immediate fill)
- [ctx.order.close()](/docs/api/orders/close) — Close position
- [ctx.order.reduce()](/docs/api/orders/reduce) — Partial exit
`,
  },

  // Helpers - overview
  {
    id: 'api/helpers/overview',
    title: 'Helper Functions (q.*)',
    category: 'api',
    subcategory: 'helpers',
    tags: ['helpers', 'q', 'crossover', 'utilities', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/onBar'],
    order: 1,
    description: 'Global helper functions for strategy logic',
    content: `# Helper Functions (q.*)

QSL provides 12 global helper functions in the \`q\` namespace for common trading logic.

## Quick Reference

| Function | Signature | Returns |
|----------|-----------|---------|
| \`q.crossOver\` | \`(seriesA, seriesB, i)\` | \`boolean\` |
| \`q.crossUnder\` | \`(seriesA, seriesB, i)\` | \`boolean\` |
| \`q.highest\` | \`(series, period, i)\` | \`number\` |
| \`q.lowest\` | \`(series, period, i)\` | \`number\` |
| \`q.sma\` | \`(series, period, i)\` | \`number\` |
| \`q.isNaN\` | \`(value)\` | \`boolean\` |
| \`q.clamp\` | \`(value, min, max)\` | \`number\` |
| \`q.between\` | \`(value, min, max)\` | \`boolean\` |
| \`q.pctChange\` | \`(a, b)\` | \`number\` |
| \`q.sign\` | \`(value)\` | \`-1 \\| 0 \\| 1\` |
| \`q.abs\` | \`(value)\` | \`number\` |
| \`q.round\` | \`(value, decimals)\` | \`number\` |

---

## Crossover Detection

### q.crossOver(seriesA, seriesB, i)

Returns \`true\` when \`seriesA\` crosses above \`seriesB\` at bar \`i\`. Compares current and previous bar values to detect the crossing.

\`\`\`javascript
if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}
\`\`\`

### q.crossUnder(seriesA, seriesB, i)

Returns \`true\` when \`seriesA\` crosses below \`seriesB\` at bar \`i\`.

\`\`\`javascript
if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
  ctx.order.close('ASSET', { signal: 'sell' });
}
\`\`\`

---

## Lookback Functions

### q.highest(series, period, i)

Returns the highest value in the last \`period\` bars ending at \`i\`.

\`\`\`javascript
const highest20 = q.highest(ctx.series.high, 20, i);
\`\`\`

### q.lowest(series, period, i)

Returns the lowest value in the last \`period\` bars ending at \`i\`.

\`\`\`javascript
const lowest20 = q.lowest(ctx.series.low, 20, i);
\`\`\`

### q.sma(series, period, i)

Returns the simple moving average over the last \`period\` bars ending at \`i\`. Useful for quick calculations inside \`onBar\` without declaring a full indicator.

\`\`\`javascript
const avgVolume = q.sma(ctx.series.volume, 20, i);
if (ctx.series.volume[i] > avgVolume * 2) {
  // Volume spike detected
}
\`\`\`

---

## Validation

### q.isNaN(value)

Returns \`true\` if the value is \`NaN\`. Essential for checking whether an indicator has enough bars to produce valid output.

\`\`\`javascript
if (q.isNaN(ctx.ind.ema[i])) return; // Indicator not ready
\`\`\`

---

## Math Utilities

### q.clamp(value, min, max)

Constrains a value within the range \`[min, max]\`.

\`\`\`javascript
const size = q.clamp(rawSize, 0.1, 5); // Limit position size
\`\`\`

### q.between(value, min, max)

Returns \`true\` if value is within \`[min, max]\` (inclusive).

\`\`\`javascript
if (q.between(ctx.ind.rsi[i], 30, 70)) {
  // RSI in neutral zone
}
\`\`\`

### q.pctChange(a, b)

Returns the percentage change from \`b\` to \`a\` as a decimal (e.g., 0.05 for 5%).

\`\`\`javascript
const change = q.pctChange(ctx.series.close[i], ctx.series.close[i - 1]);
if (change > 0.03) {
  // Price jumped more than 3%
}
\`\`\`

### q.sign(value)

Returns \`-1\`, \`0\`, or \`1\` indicating the sign of the value.

\`\`\`javascript
const direction = q.sign(ctx.ind.macd.histogram[i]);
\`\`\`

### q.abs(value)

Returns the absolute value.

\`\`\`javascript
const spread = q.abs(ctx.ind.fastEma[i] - ctx.ind.slowEma[i]);
\`\`\`

### q.round(value, decimals)

Rounds a value to the specified number of decimal places.

\`\`\`javascript
const price = q.round(ctx.series.close[i] * 1.02, 2); // 2% above, 2 decimals
\`\`\`

---

## Example: Using Multiple Helpers

\`\`\`javascript
function onBar(ctx, i) {
  const ema = ctx.ind.ema;
  const close = ctx.series.close;
  
  // Skip if indicator not ready
  if (q.isNaN(ema[i])) return;
  
  // Trend filter: price above 20-bar high
  const above20High = close[i] > q.highest(close, 20, i - 1);
  
  // Volume filter: above-average volume
  const highVolume = ctx.series.volume[i] > q.sma(ctx.series.volume, 20, i);
  
  // Entry on bullish crossover with filters
  if (above20High && highVolume && ctx.position('ASSET').qty === 0) {
    if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
      const size = q.clamp(1, 0.5, 3);
      ctx.order.market('ASSET', size, { signal: 'buy' });
    }
  }
  
  // Exit on bearish crossover
  if (q.crossUnder(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    ctx.order.close('ASSET', { signal: 'sell' });
  }
}
\`\`\`

## Related

- [onBar()](/docs/api/lifecycle/onBar) — Where helpers are used
- [Indicators](/docs/api/lifecycle/init) — Declaring indicators
`,
  },

  // Position
  {
    id: 'api/position/current',
    title: 'ctx.position()',
    category: 'api',
    subcategory: 'position',
    tags: ['position', 'qty', 'avgPrice', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'api/orders/close'],
    order: 1,
    description: 'Get current position information',
    content: `# ctx.position()

Get information about the current position.

## Signature

\`\`\`javascript
ctx.position(symbol)
\`\`\`

## Returns

| Property | Type | Description |
|----------|------|-------------|
| \`qty\` | \`number\` | Position quantity (+ long, - short, 0 flat) |
| \`avgPrice\` | \`number\` | Average entry price |
| \`side\` | \`string\` | \`'long'\`, \`'short'\`, or \`'flat'\` |

## Example

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  // Check position state
  if (pos.qty === 0) {
    // Flat - look for entries
  } else if (pos.qty > 0) {
    // Long position
    const unrealizedPnl = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice;
    
    if (unrealizedPnl > 0.05) {
      ctx.order.close('ASSET', { signal: 'takeProfit' });
    }
    if (unrealizedPnl < -0.02) {
      ctx.order.close('ASSET', { signal: 'stopLoss' });
    }
  } else if (pos.qty < 0) {
    // Short position
    const unrealizedPnl = (pos.avgPrice - ctx.series.close[i]) / pos.avgPrice;
    
    if (unrealizedPnl > 0.05) {
      ctx.order.close('ASSET', { signal: 'takeProfit' });
    }
  }
}
\`\`\`

## Common Patterns

### Position-aware entries

\`\`\`javascript
if (pos.qty === 0 && entryCondition) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
}
\`\`\`

### Calculate unrealized PnL

\`\`\`javascript
const pos = ctx.position('ASSET');
if (pos.qty > 0) {
  const pnlPercent = (ctx.series.close[i] - pos.avgPrice) / pos.avgPrice * 100;
}
\`\`\`

### Track entry bar

\`\`\`javascript
function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  // Just entered a position
  if (pos.qty > 0 && ctx.state.prevQty === 0) {
    ctx.state.entryBar = i;
    ctx.state.entryPrice = pos.avgPrice;
  }
  
  ctx.state.prevQty = pos.qty;
}
\`\`\`

## Related

- [ctx.order.market()](/docs/api/orders/market) — Enter positions
- [ctx.order.close()](/docs/api/orders/close) — Exit positions
`,
  },

  // Series
  {
    id: 'api/series/price',
    title: 'ctx.series',
    category: 'api',
    subcategory: 'series',
    tags: ['series', 'price', 'ohlcv', 'candle', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/onBar', 'api/helpers/overview'],
    order: 1,
    description: 'Access price and volume data',
    content: `# ctx.series

Access historical price and volume data.

## Available Series

| Property | Type | Description |
|----------|------|-------------|
| \`ctx.series.open\` | \`number[]\` | Opening prices |
| \`ctx.series.high\` | \`number[]\` | High prices |
| \`ctx.series.low\` | \`number[]\` | Low prices |
| \`ctx.series.close\` | \`number[]\` | Closing prices |
| \`ctx.series.volume\` | \`number[]\` | Volume |

## Accessing Data

\`\`\`javascript
function onBar(ctx, i) {
  // Current bar
  const open = ctx.series.open[i];
  const high = ctx.series.high[i];
  const low = ctx.series.low[i];
  const close = ctx.series.close[i];
  const volume = ctx.series.volume[i];
  
  // Previous bars
  const prevClose = ctx.series.close[i - 1];
  const close5ago = ctx.series.close[i - 5];
}
\`\`\`

## Common Patterns

### Bar range

\`\`\`javascript
const range = ctx.series.high[i] - ctx.series.low[i];
const bodySize = Math.abs(ctx.series.close[i] - ctx.series.open[i]);
\`\`\`

### Bullish/bearish bar

\`\`\`javascript
const isBullish = ctx.series.close[i] > ctx.series.open[i];
const isBearish = ctx.series.close[i] < ctx.series.open[i];
\`\`\`

### Price change

\`\`\`javascript
if (i > 0) {
  const change = ctx.series.close[i] - ctx.series.close[i - 1];
  const changePercent = change / ctx.series.close[i - 1] * 100;
}
\`\`\`

### Volume analysis

\`\`\`javascript
const avgVolume = q.avg(ctx.series.volume, 20, i);
const volumeRatio = ctx.series.volume[i] / avgVolume;

if (volumeRatio > 2) {
  // Volume spike - 2x average
}
\`\`\`

### New high/low detection

\`\`\`javascript
const highest20 = q.highest(ctx.series.high, 20, i - 1);
const lowest20 = q.lowest(ctx.series.low, 20, i - 1);

if (ctx.series.high[i] > highest20) {
  // New 20-bar high
}
if (ctx.series.low[i] < lowest20) {
  // New 20-bar low
}
\`\`\`

## Boundary Checks

Always check bounds when accessing previous bars:

\`\`\`javascript
function onBar(ctx, i) {
  if (i < 20) return; // Need 20 bars of history
  
  const ma20 = q.avg(ctx.series.close, 20, i);
}
\`\`\`

## Related

- [onBar()](/docs/api/lifecycle/onBar) — Where series is accessed
- [Helper Functions](/docs/api/helpers/overview) — q.highest(), q.lowest(), etc.
`,
  },
  
  // Indicator - DONCHIAN
  {
    id: 'api/indicators/donchian',
    title: 'DONCHIAN (Donchian Channel)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'donchian', 'channel', 'trend', 'breakout', 'baseline', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/init', 'api/indicators/ppo', 'examples/tdr-strategy'],
    order: 10,
    description: 'Donchian Channel indicator for trend direction and breakout detection',
    content: `# DONCHIAN (Donchian Channel)

The Donchian Channel plots the highest high and lowest low over a lookback period, with a midline that serves as a trend baseline.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('channel', 'DONCHIAN', { period: 50 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period for high/low |

## Output

Returns an object with three series:

| Property | Description |
|----------|-------------|
| \`upper\` | Highest high over the period |
| \`middle\` | Midpoint: (upper + lower) / 2 |
| \`lower\` | Lowest low over the period |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  // Multi-output indicators use underscore naming
  const upper = ctx.ind.channel_upper[i];
  const middle = ctx.ind.channel_middle[i];
  const lower = ctx.ind.channel_lower[i];
}
\`\`\`

## Use Cases

### Trend Direction (Baseline Strategy)

The middle line serves as a dynamic baseline for trend detection:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: 21, source: 'close' });
  ctx.indicator('baseline', 'DONCHIAN', { period: 50 });
}

function onBar(ctx, i) {
  const ema = ctx.ind.ema;
  const baseline = ctx.ind.baseline_middle;
  
  // EMA above baseline = bullish trend
  if (q.crossOver(ema, baseline, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'trend_bullish' });
  }
  
  // EMA below baseline = bearish trend
  if (q.crossUnder(ema, baseline, i)) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'trend_bearish' });
  }
}
\`\`\`

### Breakout Detection

Trade breakouts above the upper channel or below the lower channel:

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const prevClose = ctx.series.close[i - 1];
  const upper = ctx.ind.channel_upper[i];
  const lower = ctx.ind.channel_lower[i];
  
  // Bullish breakout
  if (prevClose <= upper && close > upper) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'breakout_high' });
  }
}
\`\`\`

### Multi-Timeframe Baseline

Use different periods for entry and exit signals:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('slowBaseline', 'DONCHIAN', { period: 50 });   // Trend
  ctx.indicator('fastBaseline', 'DONCHIAN', { period: 26 });   // Partial signals
}
\`\`\`

## Calculation

Upper = Highest(High, period)
Lower = Lowest(Low, period)
Middle = (Upper + Lower) / 2

## Related

- [PPO Indicator](/docs/api/indicators/ppo) - Risk oscillator often used with Donchian
- [TDR Strategy](/docs/examples/tdr-strategy) - Full example using Donchian baseline
- [init()](/docs/api/lifecycle/init) - Indicator declaration
`,
  },
  
  // Indicator - PPO
  {
    id: 'api/indicators/ppo',
    title: 'PPO (Percent Price Oscillator)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'ppo', 'momentum', 'oscillator', 'risk', 'laguerre', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/init', 'api/indicators/donchian', 'examples/tdr-strategy'],
    order: 11,
    description: 'Percent Price Oscillator for momentum and risk zone detection',
    content: `# PPO (Percent Price Oscillator)

The PPO measures momentum as the percentage difference between two moving averages. In Quanthop, PPO uses Laguerre filters for smoother, less noisy signals.

## Declaration

\`\`\`javascript
function init(ctx) {
  // Standard PPO
  ctx.indicator('ppo', 'PPO', { 
    fastPeriod: 12, 
    slowPeriod: 26, 
    signalPeriod: 9 
  });
  
  // Laguerre-based PPO (smoother, used in TDR)
  ctx.indicator('ppoRisk', 'PPO', { 
    shortGamma: 0.4, 
    longGamma: 0.8, 
    accuracy: 10,
    source: 'close' 
  });
}
\`\`\`

## Options

### Standard Mode

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`fastPeriod\` | \`number\` | 12 | Fast EMA period |
| \`slowPeriod\` | \`number\` | 26 | Slow EMA period |
| \`signalPeriod\` | \`number\` | 9 | Signal line period |
| \`source\` | \`string\` | 'close' | Price source |

### Laguerre Mode

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`shortGamma\` | \`number\` | 0.4 | Fast Laguerre filter gamma (0-1) |
| \`longGamma\` | \`number\` | 0.8 | Slow Laguerre filter gamma (0-1) |
| \`accuracy\` | \`number\` | 10 | Calculation precision (higher = more bars) |
| \`source\` | \`string\` | 'close' | Price source |

## Output

Returns a single series of values (typically -100 to +100 range):

\`\`\`javascript
function onBar(ctx, i) {
  const ppoValue = ctx.ind.ppo[i];
  
  // Value interpretation:
  // Positive: Price above its average (bullish momentum)
  // Negative: Price below its average (bearish momentum)
  // Extreme values: Overbought/oversold conditions
}
\`\`\`

## Use Cases

### Risk Zone Detection

Define risk zones for position management:

\`\`\`javascript
function define(ctx) {
  ctx.param('lowRiskThreshold', { type: 'int', default: -85, min: -100, max: -50 });
  ctx.param('highRiskThreshold', { type: 'int', default: 85, min: 50, max: 100 });
}

function onBar(ctx, i) {
  const ppo = ctx.ind.ppo[i];
  
  if (ppo < ctx.p.lowRiskThreshold) {
    // LOW RISK ZONE: Good for entries
    // Market is oversold, potential reversal
  } else if (ppo > ctx.p.highRiskThreshold) {
    // HIGH RISK ZONE: Consider taking profits
    // Market is overbought, extended move
  } else {
    // NEUTRAL ZONE: Normal market conditions
  }
}
\`\`\`

### Zone Duration Trading

Track how long the market stays in a risk zone:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ppo', 'PPO', { shortGamma: 0.4, longGamma: 0.8, accuracy: 10 });
  ctx.state.lowRiskBars = 0;
  ctx.state.avgLowRiskLength = 0;
  ctx.state.lowRiskZoneCount = 0;
}

function onBar(ctx, i) {
  const ppo = ctx.ind.ppo[i];
  const inLowRisk = ppo < -85;
  
  if (inLowRisk) {
    ctx.state.lowRiskBars++;
    
    // Entry signal: in zone for average duration
    if (ctx.state.lowRiskBars === Math.round(ctx.state.avgLowRiskLength)) {
      ctx.order.market('ASSET', 0.1, { signal: 'lowRiskEntry' });
    }
  } else if (ctx.state.lowRiskBars > 0) {
    // Zone ended - update average
    ctx.state.lowRiskZoneCount++;
    ctx.state.avgLowRiskLength = (
      ctx.state.avgLowRiskLength * (ctx.state.lowRiskZoneCount - 1) + 
      ctx.state.lowRiskBars
    ) / ctx.state.lowRiskZoneCount;
    ctx.state.lowRiskBars = 0;
  }
}
\`\`\`

### Momentum Confirmation

Use PPO to confirm trend signals:

\`\`\`javascript
function onBar(ctx, i) {
  const ema = ctx.ind.ema;
  const baseline = ctx.ind.baseline_middle;
  const ppo = ctx.ind.ppo[i];
  
  // Only enter long if PPO not in high-risk zone
  if (q.crossOver(ema, baseline, i) && ppo < 70) {
    ctx.order.market('ASSET', 1, { signal: 'buy' });
  }
}
\`\`\`

## Value Ranges

| Range | Interpretation |
|-------|----------------|
| < -85 | Strongly oversold (low risk for longs) |
| -85 to -50 | Moderately oversold |
| -50 to 0 | Weak bearish momentum |
| 0 to 50 | Weak bullish momentum |
| 50 to 85 | Moderately overbought |
| > 85 | Strongly overbought (high risk for longs) |

## Related

- [Donchian Indicator](/docs/api/indicators/donchian) - Often paired with PPO for trend + risk
- [TDR Strategy](/docs/examples/tdr-strategy) - Complete example using PPO risk zones
- [init()](/docs/api/lifecycle/init) - Indicator declaration
`,
  },

  // Indicator - SMA
  {
    id: 'api/indicators/sma',
    title: 'SMA (Simple Moving Average)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'sma', 'moving-average', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/ema', 'api/indicators/wma', 'api/indicators/dema'],
    order: 12,
    description: 'Simple Moving Average - the arithmetic mean of the last N prices',
    content: `# SMA (Simple Moving Average)

The SMA calculates the arithmetic mean of a price series over a lookback period. It is the simplest and most widely used moving average.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('sma50', 'SMA', { period: 50, source: 'close' });
  ctx.indicator('sma200', 'SMA', { period: 200, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Number of bars to average |
| \`source\` | \`string\` | \`'close'\` | Price source (\`open\`, \`high\`, \`low\`, \`close\`, \`hl2\`, \`hlc3\`, \`ohlc4\`) |

## Output

Returns a single \`number[]\` series.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const smaValue = ctx.ind.sma50[i];
  const prevSma = ctx.ind.sma50[i - 1];
}
\`\`\`

## Use Cases

### Golden Cross / Death Cross

\`\`\`javascript
function init(ctx) {
  ctx.indicator('sma50', 'SMA', { period: 50 });
  ctx.indicator('sma200', 'SMA', { period: 200 });
}

function onBar(ctx, i) {
  if (q.crossOver(ctx.ind.sma50, ctx.ind.sma200, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'golden_cross' });
  }
  if (q.crossUnder(ctx.ind.sma50, ctx.ind.sma200, i)) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'death_cross' });
  }
}
\`\`\`

## Calculation

$$SMA = \\frac{1}{N} \\sum_{i=0}^{N-1} Price_{t-i}$$

## Related

- [EMA](/docs/api/indicators/ema) - Exponentially weighted, faster response
- [WMA](/docs/api/indicators/wma) - Linearly weighted moving average
- [DEMA](/docs/api/indicators/dema) - Double EMA for reduced lag
`,
  },

  // Indicator - EMA
  {
    id: 'api/indicators/ema',
    title: 'EMA (Exponential Moving Average)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'ema', 'moving-average', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/sma', 'api/indicators/dema', 'api/indicators/tema'],
    order: 13,
    description: 'Exponential Moving Average - gives more weight to recent prices',
    content: `# EMA (Exponential Moving Average)

The EMA applies exponentially decreasing weights to older prices, making it more responsive to recent price changes than the SMA.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('fastEma', 'EMA', { period: ctx.p.fastLength, source: 'close' });
  ctx.indicator('slowEma', 'EMA', { period: ctx.p.slowLength, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source (\`open\`, \`high\`, \`low\`, \`close\`, \`hl2\`, \`hlc3\`, \`ohlc4\`) |

## Output

Returns a single \`number[]\` series.

## Use Cases

### EMA Crossover Strategy

\`\`\`javascript
function init(ctx) {
  ctx.indicator('fast', 'EMA', { period: 9, source: 'close' });
  ctx.indicator('slow', 'EMA', { period: 21, source: 'close' });
}

function onBar(ctx, i) {
  if (q.crossOver(ctx.ind.fast, ctx.ind.slow, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ema_cross_up' });
  }
  if (q.crossUnder(ctx.ind.fast, ctx.ind.slow, i)) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'ema_cross_down' });
  }
}
\`\`\`

### Dynamic Support/Resistance

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const ema = ctx.ind.ema21[i];
  
  // Price bouncing off EMA as support
  if (close > ema && ctx.series.low[i] <= ema * 1.001) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ema_bounce' });
  }
}
\`\`\`

## Calculation

$$EMA_t = Price_t \\times k + EMA_{t-1} \\times (1 - k)$$

Where $k = \\frac{2}{period + 1}$

## Related

- [SMA](/docs/api/indicators/sma) - Simpler, equal-weight average
- [DEMA](/docs/api/indicators/dema) - Reduced lag with double smoothing
- [TEMA](/docs/api/indicators/tema) - Further reduced lag with triple smoothing
`,
  },

  // Indicator - WMA
  {
    id: 'api/indicators/wma',
    title: 'WMA (Weighted Moving Average)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'wma', 'moving-average', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/sma', 'api/indicators/ema'],
    order: 14,
    description: 'Weighted Moving Average - linearly weighted toward recent prices',
    content: `# WMA (Weighted Moving Average)

The WMA assigns linearly increasing weights to more recent prices. The most recent price gets the highest weight, the second most recent gets a slightly lower weight, and so on.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('wma', 'WMA', { period: 20, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source (\`open\`, \`high\`, \`low\`, \`close\`, \`hl2\`, \`hlc3\`, \`ohlc4\`) |

## Output

Returns a single \`number[]\` series.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const wmaValue = ctx.ind.wma[i];
}
\`\`\`

## Use Cases

### Comparing Moving Average Types

\`\`\`javascript
function init(ctx) {
  ctx.indicator('sma', 'SMA', { period: 20 });
  ctx.indicator('ema', 'EMA', { period: 20 });
  ctx.indicator('wma', 'WMA', { period: 20 });
}

function onBar(ctx, i) {
  // WMA responds faster than SMA but slower than EMA
  // Useful as a middle ground between SMA and EMA
}
\`\`\`

## Calculation

$$WMA = \\frac{\\sum_{i=0}^{N-1} (N - i) \\times Price_{t-i}}{\\sum_{i=0}^{N-1} (N - i)}$$

## Related

- [SMA](/docs/api/indicators/sma) - Equal-weight moving average
- [EMA](/docs/api/indicators/ema) - Exponentially weighted
- [DEMA](/docs/api/indicators/dema) - Double EMA for reduced lag
`,
  },

  // Indicator - DEMA
  {
    id: 'api/indicators/dema',
    title: 'DEMA (Double Exponential Moving Average)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'dema', 'moving-average', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/ema', 'api/indicators/tema', 'api/indicators/sma'],
    order: 15,
    description: 'Double Exponential Moving Average - reduced lag via double smoothing',
    content: `# DEMA (Double Exponential Moving Average)

DEMA reduces the lag inherent in traditional moving averages by applying the EMA formula twice and adjusting. It is more responsive to price changes than a standard EMA.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('dema', 'DEMA', { period: 21, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source (\`open\`, \`high\`, \`low\`, \`close\`, \`hl2\`, \`hlc3\`, \`ohlc4\`) |

## Output

Returns a single \`number[]\` series.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const demaValue = ctx.ind.dema[i];
}
\`\`\`

## Use Cases

### Fast Trend Detection

\`\`\`javascript
function init(ctx) {
  ctx.indicator('dema', 'DEMA', { period: 21 });
  ctx.indicator('ema', 'EMA', { period: 21 });
}

function onBar(ctx, i) {
  // DEMA turns before EMA at trend reversals
  if (q.crossOver(ctx.ind.dema, ctx.ind.ema, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'dema_leads' });
  }
}
\`\`\`

## Calculation

$$DEMA = 2 \\times EMA(Price) - EMA(EMA(Price))$$

## Related

- [EMA](/docs/api/indicators/ema) - Standard exponential moving average
- [TEMA](/docs/api/indicators/tema) - Triple EMA for even less lag
- [SMA](/docs/api/indicators/sma) - Simple moving average
`,
  },

  // Indicator - TEMA
  {
    id: 'api/indicators/tema',
    title: 'TEMA (Triple Exponential Moving Average)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'tema', 'moving-average', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/ema', 'api/indicators/dema', 'api/indicators/sma'],
    order: 16,
    description: 'Triple Exponential Moving Average - minimal lag through triple smoothing',
    content: `# TEMA (Triple Exponential Moving Average)

TEMA further reduces lag by applying EMA three times and combining the results. It is the most responsive of the standard moving average family.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('tema', 'TEMA', { period: 14, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source (\`open\`, \`high\`, \`low\`, \`close\`, \`hl2\`, \`hlc3\`, \`ohlc4\`) |

## Output

Returns a single \`number[]\` series.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const temaValue = ctx.ind.tema[i];
}
\`\`\`

## Use Cases

### Scalping Entries with Minimal Lag

\`\`\`javascript
function init(ctx) {
  ctx.indicator('tema', 'TEMA', { period: 8 });
  ctx.indicator('sma', 'SMA', { period: 50 });
}

function onBar(ctx, i) {
  // TEMA for fast entry, SMA for trend filter
  const trendUp = ctx.ind.sma[i] > ctx.ind.sma[i - 5];
  if (trendUp && q.crossOver(ctx.series.close, ctx.ind.tema, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'tema_reclaim' });
  }
}
\`\`\`

## Calculation

$$TEMA = 3 \\times EMA_1 - 3 \\times EMA_2 + EMA_3$$

Where $EMA_1 = EMA(Price)$, $EMA_2 = EMA(EMA_1)$, $EMA_3 = EMA(EMA_2)$

## Related

- [DEMA](/docs/api/indicators/dema) - Double EMA, slightly more lag
- [EMA](/docs/api/indicators/ema) - Standard exponential moving average
- [TRIX](/docs/api/indicators/trix) - Rate of change of TEMA
`,
  },

  // Indicator - MACD
  {
    id: 'api/indicators/macd',
    title: 'MACD (Moving Average Convergence Divergence)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'macd', 'momentum', 'trend', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/ema', 'api/indicators/ppo', 'api/indicators/rsi'],
    order: 17,
    description: 'MACD - trend-following momentum indicator using EMA convergence/divergence',
    content: `# MACD (Moving Average Convergence Divergence)

MACD is one of the most popular technical indicators. It shows the relationship between two EMAs and provides momentum, trend direction, and signal line crossover information.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('macd', 'MACD', { 
    fastPeriod: 12, 
    slowPeriod: 26, 
    signalPeriod: 9 
  });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`fastPeriod\` | \`number\` | 12 | Fast EMA period |
| \`slowPeriod\` | \`number\` | 26 | Slow EMA period |
| \`signalPeriod\` | \`number\` | 9 | Signal line EMA period |

## Output

Returns an object with three series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`line\` | MACD line (fast EMA - slow EMA) |
| \`signal\` | Signal line (EMA of MACD line) |
| \`histogram\` | Histogram (MACD line - signal line) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const macdLine = ctx.ind.macd_line[i];
  const signal = ctx.ind.macd_signal[i];
  const histogram = ctx.ind.macd_histogram[i];
}
\`\`\`

## Use Cases

### Signal Line Crossover

\`\`\`javascript
function onBar(ctx, i) {
  if (q.crossOver(ctx.ind.macd_line, ctx.ind.macd_signal, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'macd_cross_up' });
  }
  if (q.crossUnder(ctx.ind.macd_line, ctx.ind.macd_signal, i)) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'macd_cross_down' });
  }
}
\`\`\`

### Histogram Momentum

\`\`\`javascript
function onBar(ctx, i) {
  const hist = ctx.ind.macd_histogram[i];
  const prevHist = ctx.ind.macd_histogram[i - 1];
  
  // Rising histogram = increasing bullish momentum
  if (hist > 0 && hist > prevHist) {
    // Strong bullish momentum
  }
}
\`\`\`

## Calculation

- MACD Line = EMA(fast) - EMA(slow)
- Signal Line = EMA(MACD Line, signalPeriod)
- Histogram = MACD Line - Signal Line

## Related

- [EMA](/docs/api/indicators/ema) - Underlying EMA calculations
- [PPO](/docs/api/indicators/ppo) - Percentage-based momentum oscillator
- [RSI](/docs/api/indicators/rsi) - Another popular momentum indicator
`,
  },

  // Indicator - RSI
  {
    id: 'api/indicators/rsi',
    title: 'RSI (Relative Strength Index)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'rsi', 'momentum', 'oscillator', 'overbought', 'oversold', 'qsl'],
    version: '2.0',
    related: ['api/indicators/stochrsi', 'api/indicators/stoch', 'api/indicators/cci'],
    order: 18,
    description: 'Relative Strength Index - measures speed and magnitude of price changes',
    content: `# RSI (Relative Strength Index)

The RSI measures the speed and magnitude of recent price changes on a 0-100 scale. It identifies overbought (>70) and oversold (<30) conditions.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('rsi', 'RSI', { period: 14, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 14 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source |

## Output

Returns a single \`number[]\` series with values between 0 and 100.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const rsiValue = ctx.ind.rsi[i];
}
\`\`\`

## Use Cases

### Overbought / Oversold

\`\`\`javascript
function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  
  if (rsi < 30) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'oversold' });
  }
  if (rsi > 70) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'overbought' });
  }
}
\`\`\`

### RSI Divergence

\`\`\`javascript
function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  const close = ctx.series.close[i];
  const prevRsi = ctx.ind.rsi[i - 5];
  const prevClose = ctx.series.close[i - 5];
  
  // Bullish divergence: price makes lower low, RSI makes higher low
  if (close < prevClose && rsi > prevRsi && rsi < 40) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'bullish_divergence' });
  }
}
\`\`\`

## Value Ranges

| Range | Interpretation |
|-------|----------------|
| 0 - 30 | Oversold |
| 30 - 70 | Neutral |
| 70 - 100 | Overbought |

## Calculation

$$RSI = 100 - \\frac{100}{1 + RS}$$

Where $RS = \\frac{\\text{Average Gain}}{\\text{Average Loss}}$

## Related

- [Stochastic RSI](/docs/api/indicators/stochrsi) - RSI fed into stochastic formula
- [CCI](/docs/api/indicators/cci) - Another mean-reversion oscillator
- [Williams %R](/docs/api/indicators/williamsr) - Similar overbought/oversold indicator
`,
  },

  // Indicator - STOCH
  {
    id: 'api/indicators/stoch',
    title: 'STOCH (Stochastic Oscillator)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'stochastic', 'momentum', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/stochrsi', 'api/indicators/rsi', 'api/indicators/williamsr'],
    order: 19,
    description: 'Stochastic Oscillator - compares closing price to its range over a period',
    content: `# STOCH (Stochastic Oscillator)

The Stochastic Oscillator compares a closing price to its price range over a given period. It produces two lines, %K and %D, oscillating between 0 and 100.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('stoch', 'STOCH', { kPeriod: 14, dPeriod: 3 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`kPeriod\` | \`number\` | 14 | %K lookback period |
| \`dPeriod\` | \`number\` | 3 | %D smoothing period (SMA of %K) |

## Output

Returns an object with two series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`k\` | %K line (fast stochastic) |
| \`d\` | %D line (smoothed %K) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const k = ctx.ind.stoch_k[i];
  const d = ctx.ind.stoch_d[i];
}
\`\`\`

## Use Cases

### Overbought / Oversold

\`\`\`javascript
function onBar(ctx, i) {
  const k = ctx.ind.stoch_k[i];
  const d = ctx.ind.stoch_d[i];
  
  // Buy when %K crosses above %D in oversold territory
  if (k < 20 && q.crossOver(ctx.ind.stoch_k, ctx.ind.stoch_d, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'stoch_oversold_cross' });
  }
  
  // Sell when %K crosses below %D in overbought territory
  if (k > 80 && q.crossUnder(ctx.ind.stoch_k, ctx.ind.stoch_d, i)) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'stoch_overbought_cross' });
  }
}
\`\`\`

## Value Ranges

| Range | Interpretation |
|-------|----------------|
| 0 - 20 | Oversold |
| 20 - 80 | Neutral |
| 80 - 100 | Overbought |

## Calculation

$$\\%K = \\frac{Close - Lowest Low}{Highest High - Lowest Low} \\times 100$$

$\\%D = SMA(\\%K, dPeriod)$

## Related

- [Stochastic RSI](/docs/api/indicators/stochrsi) - Stochastic applied to RSI
- [RSI](/docs/api/indicators/rsi) - Momentum oscillator
- [Williams %R](/docs/api/indicators/williamsr) - Similar range-based oscillator
`,
  },

  // Indicator - STOCHRSI
  {
    id: 'api/indicators/stochrsi',
    title: 'STOCHRSI (Stochastic RSI)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'stochrsi', 'stochastic', 'rsi', 'momentum', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/rsi', 'api/indicators/stoch', 'api/indicators/williamsr'],
    order: 20,
    description: 'Stochastic RSI - applies the stochastic formula to RSI values for extra sensitivity',
    content: `# STOCHRSI (Stochastic RSI)

Stochastic RSI applies the Stochastic Oscillator formula to RSI values instead of price. This creates a more sensitive indicator that reaches overbought/oversold more frequently than standard RSI.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('stochRsi', 'STOCHRSI', { 
    rsiPeriod: 14, 
    stochPeriod: 14, 
    kPeriod: 3, 
    dPeriod: 3 
  });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`rsiPeriod\` | \`number\` | 14 | RSI calculation period |
| \`stochPeriod\` | \`number\` | 14 | Stochastic lookback period |
| \`kPeriod\` | \`number\` | 3 | %K smoothing period |
| \`dPeriod\` | \`number\` | 3 | %D smoothing period |

## Output

Returns an object with two series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`k\` | %K line (smoothed stochastic RSI) |
| \`d\` | %D line (smoothed %K) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const k = ctx.ind.stochRsi_k[i];
  const d = ctx.ind.stochRsi_d[i];
}
\`\`\`

## Use Cases

### Sensitive Reversal Detection

\`\`\`javascript
function onBar(ctx, i) {
  const k = ctx.ind.stochRsi_k[i];
  
  // StochRSI reaches extremes more often than plain RSI
  if (k < 0.1) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'stochrsi_extreme_low' });
  }
  if (k > 0.9) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'stochrsi_extreme_high' });
  }
}
\`\`\`

## Calculation

1. Compute RSI
2. Apply stochastic formula: $StochRSI = \\frac{RSI - Lowest(RSI)}{Highest(RSI) - Lowest(RSI)}$
3. %K = SMA(StochRSI, kPeriod)
4. %D = SMA(%K, dPeriod)

## Related

- [RSI](/docs/api/indicators/rsi) - Standard RSI
- [STOCH](/docs/api/indicators/stoch) - Standard stochastic oscillator
- [Williams %R](/docs/api/indicators/williamsr) - Range-based oscillator
`,
  },

  // Indicator - ADX
  {
    id: 'api/indicators/adx',
    title: 'ADX (Average Directional Index)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'adx', 'trend', 'strength', 'directional', 'qsl'],
    version: '2.0',
    related: ['api/indicators/macd', 'api/indicators/psar', 'api/indicators/atr'],
    order: 21,
    description: 'Average Directional Index - measures trend strength regardless of direction',
    content: `# ADX (Average Directional Index)

The ADX measures trend strength on a 0-100 scale regardless of trend direction. It is accompanied by +DI and -DI lines that indicate the direction of the trend.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('adx', 'ADX', { period: 14 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 14 | Smoothing period for DI and ADX |

## Output

Returns an object with three series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`adx\` | ADX value (trend strength, 0-100) |
| \`pdi\` | +DI (positive directional indicator) |
| \`mdi\` | -DI (minus directional indicator) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const adxValue = ctx.ind.adx[i];     // Just the ADX (no prefix for primary)
  const plusDI = ctx.ind.adx_pdi[i];
  const minusDI = ctx.ind.adx_mdi[i];
}
\`\`\`

## Use Cases

### Trend Strength Filter

\`\`\`javascript
function onBar(ctx, i) {
  const adxValue = ctx.ind.adx[i];
  
  // Only trade when trend is strong
  if (adxValue > 25) {
    // Strong trend - use trend-following strategies
    if (q.crossOver(ctx.ind.fast, ctx.ind.slow, i)) {
      ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'strong_trend_entry' });
    }
  }
  // ADX < 20: range-bound, use mean-reversion
}
\`\`\`

### Directional Movement Crossover

\`\`\`javascript
function onBar(ctx, i) {
  const adxValue = ctx.ind.adx[i];
  
  if (adxValue > 20) {
    if (q.crossOver(ctx.ind.adx_pdi, ctx.ind.adx_mdi, i)) {
      ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'pdi_cross_up' });
    }
    if (q.crossUnder(ctx.ind.adx_pdi, ctx.ind.adx_mdi, i)) {
      ctx.order.close('ASSET', { signal: 'sell', reason: 'pdi_cross_down' });
    }
  }
}
\`\`\`

## Value Ranges

| ADX Value | Trend Strength |
|-----------|----------------|
| 0 - 20 | Absent or weak trend |
| 20 - 40 | Developing trend |
| 40 - 60 | Strong trend |
| 60 - 100 | Extremely strong trend |

## Related

- [MACD](/docs/api/indicators/macd) - Trend and momentum indicator
- [PSAR](/docs/api/indicators/psar) - Parabolic SAR for trend direction
- [ATR](/docs/api/indicators/atr) - Volatility measure, often used with ADX
`,
  },

  // Indicator - CCI
  {
    id: 'api/indicators/cci',
    title: 'CCI (Commodity Channel Index)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'cci', 'momentum', 'oscillator', 'mean-reversion', 'qsl'],
    version: '2.0',
    related: ['api/indicators/rsi', 'api/indicators/williamsr', 'api/indicators/stoch'],
    order: 22,
    description: 'Commodity Channel Index - measures deviation from the statistical mean',
    content: `# CCI (Commodity Channel Index)

The CCI measures how far the current price deviates from its historical average. Values above +100 suggest overbought conditions, below -100 suggest oversold.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('cci', 'CCI', { period: 20 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | Lookback period |

## Output

Returns a single \`number[]\` series. Values are unbounded but typically range from -200 to +200.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const cciValue = ctx.ind.cci[i];
}
\`\`\`

## Use Cases

### Overbought / Oversold

\`\`\`javascript
function onBar(ctx, i) {
  const cci = ctx.ind.cci[i];
  
  if (cci < -100) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'cci_oversold' });
  }
  if (cci > 100) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'cci_overbought' });
  }
}
\`\`\`

### Zero-Line Crossover

\`\`\`javascript
function onBar(ctx, i) {
  const cci = ctx.ind.cci[i];
  const prevCci = ctx.ind.cci[i - 1];
  
  if (prevCci < 0 && cci > 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'cci_cross_zero' });
  }
}
\`\`\`

## Calculation

$$CCI = \\frac{TP - SMA(TP)}{0.015 \\times MeanDeviation}$$

Where $TP = \\frac{High + Low + Close}{3}$ (Typical Price)

## Related

- [RSI](/docs/api/indicators/rsi) - Momentum oscillator with fixed 0-100 range
- [Williams %R](/docs/api/indicators/williamsr) - Another mean-reversion indicator
- [STOCH](/docs/api/indicators/stoch) - Range-based oscillator
`,
  },

  // Indicator - WILLIAMSR
  {
    id: 'api/indicators/williamsr',
    title: 'WILLIAMSR (Williams %R)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'williamsr', 'williams', 'momentum', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/stoch', 'api/indicators/rsi', 'api/indicators/cci'],
    order: 23,
    description: 'Williams %R - fast oscillator measuring overbought/oversold on a -100 to 0 scale',
    content: `# WILLIAMSR (Williams %R)

Williams %R is a fast momentum oscillator that shows the current closing price relative to the high-low range over a period. It ranges from -100 (oversold) to 0 (overbought).

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('willr', 'WILLIAMSR', { period: 14 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 14 | Lookback period |

## Output

Returns a single \`number[]\` series with values between -100 and 0.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const willr = ctx.ind.willr[i];
}
\`\`\`

## Use Cases

### Reversal Trading

\`\`\`javascript
function onBar(ctx, i) {
  const willr = ctx.ind.willr[i];
  
  if (willr < -80) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'willr_oversold' });
  }
  if (willr > -20) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'willr_overbought' });
  }
}
\`\`\`

## Value Ranges

| Range | Interpretation |
|-------|----------------|
| -100 to -80 | Oversold |
| -80 to -20 | Neutral |
| -20 to 0 | Overbought |

## Calculation

$$\\%R = \\frac{Highest High - Close}{Highest High - Lowest Low} \\times -100$$

## Related

- [STOCH](/docs/api/indicators/stoch) - Similar concept with %K/%D lines
- [RSI](/docs/api/indicators/rsi) - Momentum using gains/losses
- [CCI](/docs/api/indicators/cci) - Mean deviation oscillator
`,
  },

  // Indicator - ROC
  {
    id: 'api/indicators/roc',
    title: 'ROC (Rate of Change)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'roc', 'momentum', 'rate-of-change', 'qsl'],
    version: '2.0',
    related: ['api/indicators/trix', 'api/indicators/macd', 'api/indicators/rsi'],
    order: 24,
    description: 'Rate of Change - percentage change over a lookback period',
    content: `# ROC (Rate of Change)

ROC measures the percentage change between the current price and the price N periods ago. It oscillates around zero, with positive values indicating upward momentum.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('roc', 'ROC', { period: 12, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 12 | Lookback period |
| \`source\` | \`string\` | \`'close'\` | Price source |

## Output

Returns a single \`number[]\` series (percentage values).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const roc = ctx.ind.roc[i]; // e.g., 2.5 = 2.5% gain over period
}
\`\`\`

## Use Cases

### Momentum Filter

\`\`\`javascript
function onBar(ctx, i) {
  const roc = ctx.ind.roc[i];
  
  // Only take trades when momentum is positive
  if (roc > 0 && q.crossOver(ctx.ind.fast, ctx.ind.slow, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'positive_momentum' });
  }
}
\`\`\`

### Zero-Line Cross

\`\`\`javascript
function onBar(ctx, i) {
  const roc = ctx.ind.roc[i];
  const prevRoc = ctx.ind.roc[i - 1];
  
  if (prevRoc <= 0 && roc > 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'roc_positive' });
  }
}
\`\`\`

## Calculation

$$ROC = \\frac{Price_t - Price_{t-N}}{Price_{t-N}} \\times 100$$

## Related

- [TRIX](/docs/api/indicators/trix) - Smoothed rate of change
- [MACD](/docs/api/indicators/macd) - Momentum via EMA difference
- [RSI](/docs/api/indicators/rsi) - Bounded momentum oscillator
`,
  },

  // Indicator - AO
  {
    id: 'api/indicators/ao',
    title: 'AO (Awesome Oscillator)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'ao', 'awesome', 'momentum', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/macd', 'api/indicators/roc', 'api/indicators/rsi'],
    order: 25,
    description: 'Awesome Oscillator - measures market momentum using midpoint SMAs',
    content: `# AO (Awesome Oscillator)

The Awesome Oscillator compares a 5-period SMA to a 34-period SMA of the midpoint price (HL/2). It helps identify momentum shifts and trend strength.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ao', 'AO', { fastPeriod: 5, slowPeriod: 34 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`fastPeriod\` | \`number\` | 5 | Fast SMA period |
| \`slowPeriod\` | \`number\` | 34 | Slow SMA period |

## Output

Returns a single \`number[]\` series oscillating around zero.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const ao = ctx.ind.ao[i];
}
\`\`\`

## Use Cases

### Zero-Line Cross

\`\`\`javascript
function onBar(ctx, i) {
  const ao = ctx.ind.ao[i];
  const prevAo = ctx.ind.ao[i - 1];
  
  if (prevAo <= 0 && ao > 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ao_bullish' });
  }
  if (prevAo >= 0 && ao < 0) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'ao_bearish' });
  }
}
\`\`\`

### Saucer Setup

\`\`\`javascript
function onBar(ctx, i) {
  const ao1 = ctx.ind.ao[i - 2];
  const ao2 = ctx.ind.ao[i - 1];
  const ao3 = ctx.ind.ao[i];
  
  // Bullish saucer: AO > 0, dips then rises
  if (ao1 > ao2 && ao2 < ao3 && ao3 > 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'ao_saucer' });
  }
}
\`\`\`

## Calculation

$$AO = SMA(HL/2, fastPeriod) - SMA(HL/2, slowPeriod)$$

Where $HL/2 = (High + Low) / 2$

## Related

- [MACD](/docs/api/indicators/macd) - Similar dual-MA oscillator concept
- [ROC](/docs/api/indicators/roc) - Rate of change momentum
- [RSI](/docs/api/indicators/rsi) - Bounded momentum oscillator
`,
  },

  // Indicator - BBANDS
  {
    id: 'api/indicators/bbands',
    title: 'BBANDS (Bollinger Bands)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'bbands', 'bollinger', 'volatility', 'bands', 'qsl'],
    version: '2.0',
    related: ['api/indicators/keltner', 'api/indicators/atr', 'api/indicators/donchian'],
    order: 26,
    description: 'Bollinger Bands - volatility bands around a moving average',
    content: `# BBANDS (Bollinger Bands)

Bollinger Bands consist of a middle SMA with upper and lower bands at a standard deviation distance. They expand during high volatility and contract during low volatility.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('bb', 'BBANDS', { period: 20, stdDev: 2 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | SMA lookback period |
| \`stdDev\` | \`number\` | 2 | Standard deviation multiplier |
| \`source\` | \`string\` | \`'close'\` | Price source |

## Output

Returns an object with three series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`upper\` | Upper band (SMA + stdDev x sigma) |
| \`middle\` | Middle band (SMA) |
| \`lower\` | Lower band (SMA - stdDev x sigma) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const upper = ctx.ind.bb_upper[i];
  const middle = ctx.ind.bb_middle[i];
  const lower = ctx.ind.bb_lower[i];
}
\`\`\`

## Use Cases

### Bollinger Bounce (Mean Reversion)

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const lower = ctx.ind.bb_lower[i];
  const upper = ctx.ind.bb_upper[i];
  
  if (close <= lower) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'bb_bounce_lower' });
  }
  if (close >= upper) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'bb_touch_upper' });
  }
}
\`\`\`

### Bollinger Squeeze (Breakout)

\`\`\`javascript
function onBar(ctx, i) {
  const bandwidth = (ctx.ind.bb_upper[i] - ctx.ind.bb_lower[i]) / ctx.ind.bb_middle[i];
  const prevBandwidth = (ctx.ind.bb_upper[i-1] - ctx.ind.bb_lower[i-1]) / ctx.ind.bb_middle[i-1];
  
  // Squeeze: narrow bands followed by expansion
  if (bandwidth > prevBandwidth * 1.5 && ctx.series.close[i] > ctx.ind.bb_upper[i]) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'bb_squeeze_breakout' });
  }
}
\`\`\`

## Calculation

- Middle = SMA(Close, period)
- Upper = Middle + stdDev x StdDeviation(Close, period)
- Lower = Middle - stdDev x StdDeviation(Close, period)

## Related

- [Keltner Channels](/docs/api/indicators/keltner) - ATR-based bands (less sensitive)
- [ATR](/docs/api/indicators/atr) - Volatility measure
- [Donchian](/docs/api/indicators/donchian) - High/low channel bands
`,
  },

  // Indicator - ATR
  {
    id: 'api/indicators/atr',
    title: 'ATR (Average True Range)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'atr', 'volatility', 'true-range', 'qsl'],
    version: '2.0',
    related: ['api/indicators/bbands', 'api/indicators/keltner', 'api/indicators/donchian'],
    order: 27,
    description: 'Average True Range - measures market volatility',
    content: `# ATR (Average True Range)

ATR measures market volatility by averaging the true range over a period. True Range accounts for gaps between bars, making it more accurate than simple high-low range.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('atr', 'ATR', { period: 14 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 14 | Smoothing period |

## Output

Returns a single \`number[]\` series (always positive, in price units).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const atrValue = ctx.ind.atr[i];
}
\`\`\`

## Use Cases

### ATR-Based Stop Loss

\`\`\`javascript
function onBar(ctx, i) {
  const atr = ctx.ind.atr[i];
  const close = ctx.series.close[i];
  
  // Place stop 2 ATR below entry
  const stopLoss = close - (2 * atr);
  
  if (q.crossOver(ctx.ind.fast, ctx.ind.slow, i)) {
    ctx.order.market('ASSET', 1, { 
      signal: 'buy', 
      reason: 'entry',
      stopLoss: stopLoss 
    });
  }
}
\`\`\`

### Position Sizing by Volatility

\`\`\`javascript
function onBar(ctx, i) {
  const atr = ctx.ind.atr[i];
  const close = ctx.series.close[i];
  
  // Risk 1% of capital per ATR
  const riskPerUnit = 2 * atr;
  const capitalRisk = 1000 * 0.01; // 1% of $1000
  const positionSize = capitalRisk / riskPerUnit;
}
\`\`\`

### Volatility Filter

\`\`\`javascript
function onBar(ctx, i) {
  const atr = ctx.ind.atr[i];
  const close = ctx.series.close[i];
  const atrPercent = (atr / close) * 100;
  
  // Only trade when volatility is moderate
  if (atrPercent > 1 && atrPercent < 5) {
    // Good trading conditions
  }
}
\`\`\`

## Calculation

True Range = max(High - Low, |High - prevClose|, |Low - prevClose|)

$ATR = EMA(TrueRange, period)$

## Related

- [Bollinger Bands](/docs/api/indicators/bbands) - Volatility-based bands
- [Keltner Channels](/docs/api/indicators/keltner) - Uses ATR for band width
- [Donchian](/docs/api/indicators/donchian) - Price range channel
`,
  },

  // Indicator - KELTNER
  {
    id: 'api/indicators/keltner',
    title: 'KELTNER (Keltner Channels)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'keltner', 'channel', 'volatility', 'atr', 'bands', 'qsl'],
    version: '2.0',
    related: ['api/indicators/bbands', 'api/indicators/atr', 'api/indicators/donchian'],
    order: 28,
    description: 'Keltner Channels - EMA-based bands using ATR for width',
    content: `# KELTNER (Keltner Channels)

Keltner Channels use an EMA as the middle line with upper and lower bands offset by a multiple of ATR. They are smoother than Bollinger Bands because ATR is less volatile than standard deviation.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('keltner', 'KELTNER', { period: 20, multiplier: 2 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 20 | EMA and ATR period |
| \`multiplier\` | \`number\` | 2 | ATR multiplier for band width |

## Output

Returns an object with three series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`upper\` | Upper channel (EMA + multiplier x ATR) |
| \`middle\` | Middle line (EMA) |
| \`lower\` | Lower channel (EMA - multiplier x ATR) |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const upper = ctx.ind.keltner_upper[i];
  const middle = ctx.ind.keltner_middle[i];
  const lower = ctx.ind.keltner_lower[i];
}
\`\`\`

## Use Cases

### Channel Breakout

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  
  if (close > ctx.ind.keltner_upper[i]) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'keltner_breakout' });
  }
  if (close < ctx.ind.keltner_lower[i]) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'keltner_breakdown' });
  }
}
\`\`\`

### Bollinger-Keltner Squeeze

\`\`\`javascript
function init(ctx) {
  ctx.indicator('bb', 'BBANDS', { period: 20, stdDev: 2 });
  ctx.indicator('kc', 'KELTNER', { period: 20, multiplier: 1.5 });
}

function onBar(ctx, i) {
  const bbInside = ctx.ind.bb_lower[i] > ctx.ind.kc_lower[i] && 
                   ctx.ind.bb_upper[i] < ctx.ind.kc_upper[i];
  
  // Squeeze: Bollinger Bands inside Keltner = low volatility
  // Expansion after squeeze = potential strong move
}
\`\`\`

## Calculation

- Middle = EMA(Close, period)
- Upper = Middle + multiplier x ATR(period)
- Lower = Middle - multiplier x ATR(period)

## Related

- [Bollinger Bands](/docs/api/indicators/bbands) - StdDev-based bands (more reactive)
- [ATR](/docs/api/indicators/atr) - Underlying volatility measure
- [Donchian](/docs/api/indicators/donchian) - Price-range channel
`,
  },

  // Indicator - PSAR
  {
    id: 'api/indicators/psar',
    title: 'PSAR (Parabolic SAR)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'psar', 'parabolic', 'sar', 'trend', 'stop-and-reverse', 'qsl'],
    version: '2.0',
    related: ['api/indicators/adx', 'api/indicators/macd', 'api/indicators/ema'],
    order: 29,
    description: 'Parabolic SAR - trailing stop and reverse indicator for trend following',
    content: `# PSAR (Parabolic SAR)

Parabolic SAR (Stop And Reverse) trails price with an accelerating curve. When the indicator flips from above to below price (or vice versa), it signals a potential trend reversal.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('psar', 'PSAR', { step: 0.02, max: 0.2 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`step\` | \`number\` | 0.02 | Acceleration factor increment |
| \`max\` | \`number\` | 0.2 | Maximum acceleration factor |

## Output

Returns a single \`number[]\` series (the SAR value for each bar).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const sar = ctx.ind.psar[i];
  const close = ctx.series.close[i];
  
  // SAR below price = uptrend
  // SAR above price = downtrend
  const isBullish = close > sar;
}
\`\`\`

## Use Cases

### Trend Following

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const sar = ctx.ind.psar[i];
  const prevSar = ctx.ind.psar[i - 1];
  const prevClose = ctx.series.close[i - 1];
  
  // SAR flipped from above to below price
  if (prevClose <= prevSar && close > sar) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'psar_bullish' });
  }
  
  // SAR flipped from below to above price
  if (prevClose >= prevSar && close < sar) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'psar_bearish' });
  }
}
\`\`\`

### Trailing Stop

\`\`\`javascript
function onBar(ctx, i) {
  const sar = ctx.ind.psar[i];
  
  // Use SAR as a dynamic trailing stop level
  if (ctx.position.qty > 0 && ctx.series.close[i] < sar) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'trailing_stop' });
  }
}
\`\`\`

## Calculation

The SAR value accelerates toward price using an acceleration factor (AF) that increases by \`step\` each bar (up to \`max\`) when a new extreme point is reached.

## Related

- [ADX](/docs/api/indicators/adx) - Trend strength (pair with PSAR for direction + strength)
- [MACD](/docs/api/indicators/macd) - Momentum-based trend signals
- [EMA](/docs/api/indicators/ema) - Simpler trend-following with moving averages
`,
  },

  // Indicator - TRIX
  {
    id: 'api/indicators/trix',
    title: 'TRIX (Triple EMA Oscillator)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'trix', 'momentum', 'oscillator', 'trend', 'qsl'],
    version: '2.0',
    related: ['api/indicators/tema', 'api/indicators/roc', 'api/indicators/macd'],
    order: 30,
    description: 'TRIX - rate of change of a triple-smoothed EMA, filtering out noise',
    content: `# TRIX (Triple EMA Oscillator)

TRIX shows the percentage rate of change of a triple-smoothed EMA. The triple smoothing filters out minor price movements, making TRIX useful for identifying significant trend changes.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('trix', 'TRIX', { period: 15, source: 'close' });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 15 | EMA period (applied three times) |
| \`source\` | \`string\` | \`'close'\` | Price source |

## Output

Returns a single \`number[]\` series oscillating around zero.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const trix = ctx.ind.trix[i]; // Percentage value
}
\`\`\`

## Use Cases

### Zero-Line Crossover

\`\`\`javascript
function onBar(ctx, i) {
  const trix = ctx.ind.trix[i];
  const prevTrix = ctx.ind.trix[i - 1];
  
  if (prevTrix <= 0 && trix > 0) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'trix_bullish' });
  }
  if (prevTrix >= 0 && trix < 0) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'trix_bearish' });
  }
}
\`\`\`

## Calculation

1. $EMA_1 = EMA(Price, period)$
2. $EMA_2 = EMA(EMA_1, period)$
3. $EMA_3 = EMA(EMA_2, period)$
4. $TRIX = \\frac{EMA_3[t] - EMA_3[t-1]}{EMA_3[t-1]} \\times 100$

## Related

- [TEMA](/docs/api/indicators/tema) - Triple EMA value (not rate of change)
- [ROC](/docs/api/indicators/roc) - Rate of change of raw price
- [MACD](/docs/api/indicators/macd) - Dual EMA momentum indicator
`,
  },

  // Indicator - ICHIMOKU
  {
    id: 'api/indicators/ichimoku',
    title: 'ICHIMOKU (Ichimoku Cloud)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'ichimoku', 'cloud', 'trend', 'support', 'resistance', 'qsl'],
    version: '2.0',
    related: ['api/indicators/donchian', 'api/indicators/ema', 'api/indicators/adx'],
    order: 31,
    description: 'Ichimoku Cloud - comprehensive trend system with support/resistance',
    content: `# ICHIMOKU (Ichimoku Cloud)

Ichimoku Kinko Hyo ("one glance equilibrium chart") is a comprehensive indicator system providing trend direction, support/resistance, and momentum signals through five lines.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ichimoku', 'ICHIMOKU', { 
    tenkanPeriod: 9, 
    kijunPeriod: 26, 
    senkouBPeriod: 52 
  });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`tenkanPeriod\` | \`number\` | 9 | Tenkan-sen (conversion line) period |
| \`kijunPeriod\` | \`number\` | 26 | Kijun-sen (base line) period |
| \`senkouBPeriod\` | \`number\` | 52 | Senkou Span B period |

## Output

Returns an object with five series, accessed via underscore naming:

| Property | Description |
|----------|-------------|
| \`tenkan\` | Tenkan-sen (conversion line) - fast signal |
| \`kijun\` | Kijun-sen (base line) - trend confirmation |
| \`senkouA\` | Senkou Span A - leading cloud edge |
| \`senkouB\` | Senkou Span B - lagging cloud edge |
| \`chikou\` | Chikou Span (lagging line) - close shifted back |

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const tenkan = ctx.ind.ichimoku_tenkan[i];
  const kijun = ctx.ind.ichimoku_kijun[i];
  const senkouA = ctx.ind.ichimoku_senkouA[i];
  const senkouB = ctx.ind.ichimoku_senkouB[i];
  const chikou = ctx.ind.ichimoku_chikou[i];
}
\`\`\`

## Use Cases

### TK Cross (Tenkan/Kijun Crossover)

\`\`\`javascript
function onBar(ctx, i) {
  const tenkan = ctx.ind.ichimoku_tenkan;
  const kijun = ctx.ind.ichimoku_kijun;
  const close = ctx.series.close[i];
  const cloudTop = Math.max(ctx.ind.ichimoku_senkouA[i], ctx.ind.ichimoku_senkouB[i]);
  
  // Bullish: TK cross above the cloud
  if (q.crossOver(tenkan, kijun, i) && close > cloudTop) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'tk_cross_above_cloud' });
  }
}
\`\`\`

### Cloud as Support/Resistance

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const senkouA = ctx.ind.ichimoku_senkouA[i];
  const senkouB = ctx.ind.ichimoku_senkouB[i];
  const cloudTop = Math.max(senkouA, senkouB);
  const cloudBottom = Math.min(senkouA, senkouB);
  
  if (close > cloudTop) {
    // Above cloud: bullish trend
  } else if (close < cloudBottom) {
    // Below cloud: bearish trend
  } else {
    // Inside cloud: consolidation / no-trade zone
  }
}
\`\`\`

## Calculation

- Tenkan-sen = (Highest High + Lowest Low) / 2 over tenkanPeriod
- Kijun-sen = (Highest High + Lowest Low) / 2 over kijunPeriod
- Senkou Span A = (Tenkan + Kijun) / 2
- Senkou Span B = (Highest High + Lowest Low) / 2 over senkouBPeriod
- Chikou Span = Close shifted back kijunPeriod bars

## Related

- [Donchian](/docs/api/indicators/donchian) - Underlying channel concept
- [EMA](/docs/api/indicators/ema) - Simpler trend following
- [ADX](/docs/api/indicators/adx) - Trend strength measurement
`,
  },

  // Indicator - OBV
  {
    id: 'api/indicators/obv',
    title: 'OBV (On-Balance Volume)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'obv', 'volume', 'trend', 'accumulation', 'qsl'],
    version: '2.0',
    related: ['api/indicators/adl', 'api/indicators/mfi', 'api/indicators/vwap'],
    order: 32,
    description: 'On-Balance Volume - cumulative volume indicator for trend confirmation',
    content: `# OBV (On-Balance Volume)

OBV is a cumulative volume indicator that adds volume on up-days and subtracts volume on down-days. Rising OBV confirms an uptrend; divergence between OBV and price warns of potential reversals.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('obv', 'OBV', {});
}
\`\`\`

## Options

OBV requires no options. It uses close and volume data automatically.

## Output

Returns a single \`number[]\` series (cumulative volume).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const obv = ctx.ind.obv[i];
}
\`\`\`

## Use Cases

### Trend Confirmation

\`\`\`javascript
function init(ctx) {
  ctx.indicator('obv', 'OBV', {});
  ctx.indicator('ema', 'EMA', { period: 21 });
}

function onBar(ctx, i) {
  const priceUp = ctx.series.close[i] > ctx.series.close[i - 10];
  const obvUp = ctx.ind.obv[i] > ctx.ind.obv[i - 10];
  
  // Price and OBV both rising = confirmed uptrend
  if (priceUp && obvUp) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'obv_confirmed' });
  }
  
  // Price rising but OBV falling = bearish divergence
  if (priceUp && !obvUp) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'obv_divergence' });
  }
}
\`\`\`

## Calculation

- If Close > Previous Close: OBV = Previous OBV + Volume
- If Close < Previous Close: OBV = Previous OBV - Volume
- If Close = Previous Close: OBV = Previous OBV

## Related

- [ADL](/docs/api/indicators/adl) - Accumulation/Distribution Line
- [MFI](/docs/api/indicators/mfi) - Volume-weighted RSI
- [VWAP](/docs/api/indicators/vwap) - Volume Weighted Average Price
`,
  },

  // Indicator - MFI
  {
    id: 'api/indicators/mfi',
    title: 'MFI (Money Flow Index)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'mfi', 'volume', 'momentum', 'oscillator', 'qsl'],
    version: '2.0',
    related: ['api/indicators/rsi', 'api/indicators/obv', 'api/indicators/vwap'],
    order: 33,
    description: 'Money Flow Index - volume-weighted RSI for identifying overbought/oversold',
    content: `# MFI (Money Flow Index)

The MFI is essentially RSI weighted by volume. It oscillates between 0 and 100, with readings above 80 suggesting overbought and below 20 suggesting oversold conditions.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('mfi', 'MFI', { period: 14 });
}
\`\`\`

## Options

| Option | Type | Default | Description |
|--------|------|---------|-------------|
| \`period\` | \`number\` | 14 | Lookback period |

## Output

Returns a single \`number[]\` series with values between 0 and 100.

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const mfi = ctx.ind.mfi[i];
}
\`\`\`

## Use Cases

### Overbought / Oversold with Volume Confirmation

\`\`\`javascript
function onBar(ctx, i) {
  const mfi = ctx.ind.mfi[i];
  
  // MFI accounts for volume, so signals carry more weight than RSI alone
  if (mfi < 20) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'mfi_oversold' });
  }
  if (mfi > 80) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'mfi_overbought' });
  }
}
\`\`\`

### MFI + RSI Comparison

\`\`\`javascript
function init(ctx) {
  ctx.indicator('rsi', 'RSI', { period: 14 });
  ctx.indicator('mfi', 'MFI', { period: 14 });
}

function onBar(ctx, i) {
  const rsi = ctx.ind.rsi[i];
  const mfi = ctx.ind.mfi[i];
  
  // Both signaling oversold = stronger signal
  if (rsi < 30 && mfi < 20) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'rsi_mfi_double_oversold' });
  }
}
\`\`\`

## Calculation

1. Typical Price = (High + Low + Close) / 3
2. Money Flow = Typical Price x Volume
3. Positive/Negative flows separated by direction
4. Money Flow Ratio = Positive Flow / Negative Flow
5. $MFI = 100 - \\frac{100}{1 + MFR}$

## Related

- [RSI](/docs/api/indicators/rsi) - Price-only momentum oscillator
- [OBV](/docs/api/indicators/obv) - Cumulative volume indicator
- [VWAP](/docs/api/indicators/vwap) - Volume Weighted Average Price
`,
  },

  // Indicator - VWAP
  {
    id: 'api/indicators/vwap',
    title: 'VWAP (Volume Weighted Average Price)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'vwap', 'volume', 'price', 'institutional', 'qsl'],
    version: '2.0',
    related: ['api/indicators/obv', 'api/indicators/mfi', 'api/indicators/sma'],
    order: 34,
    description: 'Volume Weighted Average Price - institutional benchmark for fair value',
    content: `# VWAP (Volume Weighted Average Price)

VWAP calculates the average price weighted by volume across the entire session. It is widely used by institutional traders as a benchmark for trade execution quality.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('vwap', 'VWAP', {});
}
\`\`\`

## Options

VWAP requires no configuration. It uses high, low, close, and volume data automatically.

## Output

Returns a single \`number[]\` series (price level).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const vwap = ctx.ind.vwap[i];
  const close = ctx.series.close[i];
  
  // Price above VWAP = bullish bias
  // Price below VWAP = bearish bias
}
\`\`\`

## Use Cases

### VWAP Trend Filter

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const vwap = ctx.ind.vwap[i];
  
  // Only take long positions when price is above VWAP
  if (close > vwap && q.crossOver(ctx.ind.fast, ctx.ind.slow, i)) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'above_vwap' });
  }
}
\`\`\`

### VWAP Bounce

\`\`\`javascript
function onBar(ctx, i) {
  const close = ctx.series.close[i];
  const low = ctx.series.low[i];
  const vwap = ctx.ind.vwap[i];
  
  // Price touched VWAP and bounced
  if (low <= vwap && close > vwap) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'vwap_bounce' });
  }
}
\`\`\`

## Calculation

$$VWAP = \\frac{\\sum (TP \\times Volume)}{\\sum Volume}$$

Where $TP = \\frac{High + Low + Close}{3}$ (Typical Price), cumulative from session start.

## Related

- [OBV](/docs/api/indicators/obv) - On-Balance Volume
- [MFI](/docs/api/indicators/mfi) - Volume-weighted RSI
- [SMA](/docs/api/indicators/sma) - Simple price average (no volume weighting)
`,
  },

  // Indicator - ADL
  {
    id: 'api/indicators/adl',
    title: 'ADL (Accumulation/Distribution Line)',
    category: 'api',
    subcategory: 'indicators',
    tags: ['indicator', 'adl', 'volume', 'accumulation', 'distribution', 'qsl'],
    version: '2.0',
    related: ['api/indicators/obv', 'api/indicators/mfi', 'api/indicators/vwap'],
    order: 35,
    description: 'Accumulation/Distribution Line - identifies cumulative money flow into/out of an asset',
    content: `# ADL (Accumulation/Distribution Line)

The ADL measures cumulative money flow by combining price and volume. Unlike OBV which uses the full volume, ADL weights the volume by where the close falls within the high-low range.

## Declaration

\`\`\`javascript
function init(ctx) {
  ctx.indicator('adl', 'ADL', {});
}
\`\`\`

## Options

ADL requires no options. It uses high, low, close, and volume data automatically.

## Output

Returns a single \`number[]\` series (cumulative).

## Accessing Values

\`\`\`javascript
function onBar(ctx, i) {
  const adl = ctx.ind.adl[i];
}
\`\`\`

## Use Cases

### Divergence Detection

\`\`\`javascript
function onBar(ctx, i) {
  const priceUp = ctx.series.close[i] > ctx.series.close[i - 10];
  const adlUp = ctx.ind.adl[i] > ctx.ind.adl[i - 10];
  
  // Price rising but ADL falling = distribution (bearish)
  if (priceUp && !adlUp) {
    ctx.order.close('ASSET', { signal: 'sell', reason: 'adl_divergence' });
  }
  
  // Price falling but ADL rising = accumulation (bullish)
  if (!priceUp && adlUp) {
    ctx.order.market('ASSET', 1, { signal: 'buy', reason: 'adl_accumulation' });
  }
}
\`\`\`

## Calculation

1. Money Flow Multiplier = $\\frac{(Close - Low) - (High - Close)}{High - Low}$
2. Money Flow Volume = Multiplier x Volume
3. ADL = cumulative sum of Money Flow Volume

## Related

- [OBV](/docs/api/indicators/obv) - Simpler cumulative volume indicator
- [MFI](/docs/api/indicators/mfi) - Volume-weighted RSI oscillator
- [VWAP](/docs/api/indicators/vwap) - Volume Weighted Average Price
`,
  },
];
